"use client";

import { useMemo, useState } from "react";

export type WaffleLegendEntry = {
  label: string;
  color: string;
  shape?: "filled" | "open";
};

export type WaffleItem = {
  /** Stable key for React reconciliation. */
  key: string;
  /** Bucket name shown in tooltip + legend. */
  status: string;
  /** Fill colour for "filled" shape, stroke colour for "open". */
  color: string;
  /** Encoding mode. CB-safe pairs use both colour AND shape. */
  shape?: "filled" | "open";
};

type Props = {
  items: WaffleItem[];
  /** Number of columns. Defaults to ceil(sqrt(N)) clamped to [10, 20]. */
  cols?: number;
  /** Cell size in px (each dot is `size` × `size`). */
  size?: number;
  /** Gap between cells in px. */
  gap?: number;
  legend?: WaffleLegendEntry[];
  /** Accessible label describing the whole grid (e.g. "92 of 120 underway"). */
  ariaLabel: string;
};

export function WaffleGrid({
  items,
  cols,
  size = 14,
  gap = 4,
  legend,
  ariaLabel,
}: Props) {
  const [hover, setHover] = useState<{
    index: number;
    px: number;
    py: number;
  } | null>(null);

  const columnCount = cols ?? defaultCols(items.length);

  const summary = useMemo(() => {
    const map = new Map<string, number>();
    for (const it of items) map.set(it.status, (map.get(it.status) ?? 0) + 1);
    return map;
  }, [items]);

  return (
    <div className="w-full">
      <div
        role="img"
        aria-label={ariaLabel}
        className="relative inline-grid"
        style={{
          gridTemplateColumns: `repeat(${columnCount}, ${size}px)`,
          gap: `${gap}px`,
        }}
        onMouseLeave={() => setHover(null)}
      >
        {items.map((it, i) => {
          const open = it.shape === "open";
          return (
            <span
              key={it.key}
              aria-hidden
              className="block"
              style={{
                width: size,
                height: size,
                borderRadius: "9999px",
                backgroundColor: open ? "transparent" : it.color,
                border: open ? `1.5px solid ${it.color}` : "none",
              }}
              onMouseEnter={(e) => updateHover(e, i, setHover)}
              onMouseMove={(e) => updateHover(e, i, setHover)}
            />
          );
        })}

        {hover && (
          <div
            className="pointer-events-none absolute border border-rule bg-bg text-ink-2 px-3 py-2 text-sm shadow-md max-w-[260px] z-10"
            style={{
              left: hover.px + 12,
              top: hover.py + 12,
            }}
          >
            {(() => {
              const it = items[hover.index];
              const count = summary.get(it.status) ?? 0;
              const total = items.length;
              const pct = total > 0 ? (count / total) * 100 : 0;
              return (
                <>
                  <div className="font-medium">{it.status}</div>
                  <div className="cs-mono text-xs mt-0.5">
                    {count} of {total} · {pct.toFixed(0)}%
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>

      {legend && legend.length > 0 && (
        <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5 text-sm list-none p-0">
          {legend.map((entry, i) => {
            const open = entry.shape === "open";
            return (
              <li
                key={`${i}-${entry.color}-${entry.label}`}
                className="flex items-center gap-2"
              >
                <span
                  aria-hidden
                  className="inline-block"
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "9999px",
                    backgroundColor: open ? "transparent" : entry.color,
                    border: open ? `1.5px solid ${entry.color}` : "none",
                  }}
                />
                <span>{entry.label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function defaultCols(n: number): number {
  if (n <= 0) return 1;
  const root = Math.ceil(Math.sqrt(n));
  return Math.min(20, Math.max(10, root));
}

function updateHover(
  e: React.MouseEvent<HTMLElement>,
  index: number,
  setHover: (h: { index: number; px: number; py: number }) => void,
) {
  const host = e.currentTarget.parentElement;
  const r = host?.getBoundingClientRect();
  if (!r) return;
  setHover({ index, px: e.clientX - r.left, py: e.clientY - r.top });
}
